import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCreateHedgeBeaconDailyUpdate } from "@/lib/api/hedgeBeaconDailyUpdates";
import {
  DailyUpdateForm,
  DailyUpdateFormValues,
  validateDailyUpdateFormValues,
} from "./DailyUpdateForm";

type CreateDailyUpdateModalProps = {
  open: boolean;
  defaultDate: string;
  onOpenChange: (open: boolean) => void;
};

export function CreateDailyUpdateModal({
  open,
  defaultDate,
  onOpenChange,
}: CreateDailyUpdateModalProps) {
  const { toast } = useToast();
  const createDailyUpdate = useCreateHedgeBeaconDailyUpdate();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleSubmit = async (values: DailyUpdateFormValues) => {
    if (isSubmitting) return;

    const validationMessage = validateDailyUpdateFormValues(values);
    if (validationMessage) {
      toast({
        title: "Error",
        description: validationMessage,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await createDailyUpdate({
        date: values.date,
        url: values.url.trim(),
      });

      if (result.success) {
        toast({
          title: "Created",
          description: "",
        });
        onOpenChange(false);
      } else {
        toast({
          title: "Failed",
          description: result.message || "Failed to create daily update",
        });
      }
    } catch (_error: unknown) {
      toast({
        title: "Error",
        description: "Failed to create daily update",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Daily Update</DialogTitle>
          <DialogDescription>
            Add a YouTube Daily Update link for a date.
          </DialogDescription>
        </DialogHeader>
        <DailyUpdateForm
          initialValues={{ date: defaultDate, url: "" }}
          submitLabel="Create Daily Update"
          submittingLabel="Saving..."
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
